import React, { useState, useCallback } from "react";
import { Receipt } from "lucide-react";
import api from "../lib/api";
import { useAutoSync } from "../hooks/useAutoSync";
import { money } from "../lib/format";
import { CATEGORY_COLORS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";

export default function Purchases() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => { const { data } = await api.get("/purchases"); setRows(data); }, []);
  useAutoSync(load, [load]);

  const groups = rows.reduce((acc, r) => {
    (acc[r.date] = acc[r.date] || []).push(r);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort().reverse();

  return (
    <div className="animate-fade-up">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Purchases</h1>
      <p className="text-sm text-slate-500">Every price you've logged, newest first</p>

      {dates.length === 0 && (
        <div className="mt-16 text-center text-slate-400"><p className="text-sm">No purchases yet.</p></div>
      )}

      <div className="mt-6 space-y-6">
        {dates.map((d) => {
          const total = groups[d].reduce((s, r) => s + (r.price || 0), 0);
          return (
            <div key={d} data-testid={`purchase-group-${d}`}>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-display font-bold text-slate-700">
                  {new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                </h3>
                <span className="font-mono text-sm font-semibold text-slate-500">{money(total, cur)}</span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                {groups[d].map((r, i) => (
                  <div key={i} className="flex items-center gap-3 border-b border-slate-100 p-3 last:border-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 text-slate-400"><Receipt className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold text-slate-800">{r.food_name}</span>
                        <Badge className={`shrink-0 border-0 text-[10px] ${CATEGORY_COLORS[r.category] || CATEGORY_COLORS.Other}`}>{r.category}</Badge>
                      </div>
                      <p className="text-xs text-slate-400">{r.package_qty} {r.package_unit}{r.store ? ` · ${r.store}` : ""}</p>
                    </div>
                    <span className="font-mono font-semibold text-slate-700">{money(r.price, cur)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
